<template>
  <div class="sankey-wrap">
    <canvas ref="sankeyCanvas"></canvas>
    <div v-if="!hasData && !loading" class="sankey-empty">
      No data to display
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
import { SankeyController, Flow } from 'chartjs-chart-sankey';

Chart.register(...registerables, SankeyController, Flow);

const NODE_COLORS = {
  Available:    '#3b82f6',
  Income:       '#10b981',
  'Transfer In':'#06b6d4',
  Expenses:     '#ef4444',
  Investments:  '#8b5cf6',
  'Transfer Out':'#f59e0b',
  'Net Gain':   '#10b981',
  Shortfall:    '#ef4444',
  // expense category display-name fallbacks
  'Gym Memberships': '#ec4899',
  'Rent':            '#f97316',
  'Daily Travel':    '#a855f7',
  'Health and Fitness':'#f43f5e',
  'Car Payment':     '#14b8a6',
  'Utilities':       '#84cc16',
  'Subscriptions':   '#6366f1',
  'Restaurants / Bars':'#f59e0b',
  'Car Insurance':   '#0ea5e9',
  'Clothing':        '#d946ef',
  'Discretionary':   '#64748b',
  'Car Maintenance': '#78716c',
  'Entertainment':   '#8b5cf6',
  'Groceries':       '#ef4444',
  'Other':           '#94a3b8',
};

function nodeColor(name) {
  return NODE_COLORS[name] || '#94a3b8';
}

export default {
  name: 'SankeyChart',
  props: {
    expenses: { type: Array, default: () => [] },
    incomes: { type: Array, default: () => [] },
    transfers: { type: Array, default: () => [] },
    savings: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
  },
  data() {
    return {
      sankeyChart: null,
      initialized: false,
      loading: false,
      renderTimer: null,
    };
  },
  computed: {
    hasData() {
      return this.incomes.length > 0 || this.expenses.length > 0;
    },
    categoryNameMap() {
      const map = {};
      (this.categories || []).forEach(c => {
        map[c.name] = c.display_name || c.name;
      });
      return map;
    },
  },
  methods: {
    displayName(internalName) {
      return this.categoryNameMap[internalName] || internalName;
    },
    // Debounce all render requests — clears any pending timer before scheduling a new one.
    // This prevents multiple watcher callbacks from spawning concurrent initChart calls.
    scheduleRender(delay) {
      if (this.renderTimer) clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => {
        this.renderTimer = null;
        this.initChart();
      }, delay || (this.initialized ? 150 : 600));
    },
    buildFlows() {
      const flows = [];
      let totalIn = 0;

      // ── Sources → Available ───────────────────────────────────────
      // All income aggregated into a single labelled "Income" node to avoid
      // per-category label collisions (e.g. 'other' clashing with transfers).
      const totalIncomeAmt = this.incomes.reduce((s, t) => s + Math.abs(t.v3_amount || 0), 0);
      if (totalIncomeAmt > 0) {
        flows.push({ from: 'Income', to: 'Available', flow: totalIncomeAmt });
        totalIn += totalIncomeAmt;
      }

      // Transfers In (negative amounts) by category label
      const transfersIn = {};
      this.transfers
        .filter(t => (t.v3_amount || 0) < 0)
        .forEach(t => {
          const key = t.v3_category !== 'other' ? this.displayName(t.v3_category) : 'Transfer In';
          const amt = Math.abs(t.v3_amount);
          transfersIn[key] = (transfersIn[key] || 0) + amt;
          totalIn += amt;
        });
      Object.entries(transfersIn).forEach(([cat, amt]) => {
        if (amt > 0) flows.push({ from: cat, to: 'Available', flow: amt });
      });

      if (totalIn === 0) return [];

      // ── Destinations (order here controls vertical position) ─────
      let totalOut = 0;

      // 1. Expenses → single "Expenses" trunk, then branch by category.
      //    Categories sorted largest→smallest so biggest is at the top.
      const expByCategory = {};
      this.expenses.forEach(t => {
        const key = this.displayName(t.v3_category) || 'Other';
        expByCategory[key] = (expByCategory[key] || 0) + Math.abs(t.v3_amount || 0);
      });
      const totalExpAmt = Object.values(expByCategory).reduce((s, v) => s + v, 0);
      if (totalExpAmt > 0) {
        flows.push({ from: 'Available', to: 'Expenses', flow: totalExpAmt });
        totalOut += totalExpAmt;
        Object.entries(expByCategory)
          .sort(([, a], [, b]) => b - a)
          .forEach(([cat, amt]) => {
            if (amt > 0) flows.push({ from: 'Expenses', to: cat, flow: amt });
          });
      }

      // 2. Investments
      const totalInvest = this.savings.reduce((s, t) => s + Math.abs(t.v3_amount || 0), 0);
      if (totalInvest > 0) {
        flows.push({ from: 'Available', to: 'Investments', flow: totalInvest });
        totalOut += totalInvest;
      }

      // 3. Transfers Out
      const transfersOut = {};
      this.transfers
        .filter(t => (t.v3_amount || 0) > 0)
        .forEach(t => {
          const key = t.v3_category !== 'other' ? this.displayName(t.v3_category) : 'Transfer Out';
          const amt = t.v3_amount;
          transfersOut[key] = (transfersOut[key] || 0) + amt;
          totalOut += amt;
        });
      Object.entries(transfersOut).forEach(([cat, amt]) => {
        if (amt > 0) flows.push({ from: 'Available', to: cat, flow: amt });
      });

      // 4. Net Gain / Shortfall — always last so it renders at the bottom
      const net = totalIn - totalOut;
      if (net > 1) {
        flows.push({ from: 'Available', to: 'Net Gain', flow: net });
      } else if (net < -1) {
        flows.push({ from: 'Shortfall', to: 'Available', flow: Math.abs(net) });
      }

      return JSON.parse(JSON.stringify(flows));
    },
    initChart() {
      const canvas = this.$refs.sankeyCanvas;
      if (!canvas) return;

      if (canvas.offsetWidth === 0 || !document.body.contains(canvas)) {
        this.scheduleRender(300);
        return;
      }

      // Tear down any existing chart and defer creation to the next animation
      // frame so any in-flight draw calls from the destroyed chart complete
      // before we touch the canvas again.
      if (this.sankeyChart) {
        try { this.sankeyChart.destroy(); } catch (e) { /* ignore */ }
        this.sankeyChart = null;
        requestAnimationFrame(() => {
          if (this.$refs.sankeyCanvas) this.initChart();
        });
        return;
      }

      // Verify the 2D context is usable before handing it to Chart.js.
      if (!canvas.getContext('2d')) {
        this.scheduleRender(300);
        return;
      }

      const flows = this.buildFlows();
      if (!flows.length) return;

      const fromNames = [...new Set(flows.map(f => f.from))];
      const toNames = [...new Set(flows.map(f => f.to))];
      const colorLookup = {};
      [...new Set([...fromNames, ...toNames])].forEach(n => { colorLookup[n] = nodeColor(n); });

      try {
        this.sankeyChart = new Chart(canvas, {
          type: 'sankey',
          data: {
            datasets: [{
              data: flows,
              colorMode: 'gradient',
              colorFrom: (c) => colorLookup[c.dataset.data[c.dataIndex]?.from] || '#94a3b8',
              colorTo: (c) => colorLookup[c.dataset.data[c.dataIndex]?.to] || '#94a3b8',
              size: 'max',
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const d = ctx.dataset.data[ctx.dataIndex];
                    const amt = d.flow.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
                    return `${d.from} → ${d.to}: ${amt}`;
                  },
                },
              },
            },
          },
        });
        this.initialized = true;
      } catch (err) {
        console.error('Sankey chart error:', err);
        // Retry once after a short delay in case of transient DOM issues
        this.scheduleRender(400);
      }
    },
  },
  watch: {
    expenses: {
      handler() {
        if (this.hasData) this.scheduleRender();
      },
      deep: true,
    },
    incomes: {
      handler() {
        if (this.hasData) this.scheduleRender();
      },
      deep: true,
    },
    transfers: {
      handler() {
        if (this.initialized) this.scheduleRender();
      },
      deep: true,
    },
    savings: {
      handler() {
        if (this.initialized) this.scheduleRender();
      },
      deep: true,
    },
  },
  mounted() {
    // Initialise if data is already available on mount (e.g. opened inside a modal
    // after the month data has loaded — watchers won't fire on initial prop values).
    if (this.hasData) this.scheduleRender();
  },
  beforeUnmount() {
    if (this.renderTimer) clearTimeout(this.renderTimer);
    if (this.sankeyChart) { this.sankeyChart.destroy(); this.sankeyChart = null; }
  },
};
</script>

<style scoped>
.sankey-wrap {
  position: relative;
  height: 380px;
  width: 100%;
}
.sankey-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
}
</style>
